import { Fragment, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, Popup, Tooltip, useMap } from 'react-leaflet';
import MapTouchGate from '../map/MapTouchGate';
import 'leaflet/dist/leaflet.css';
import type { SprayTarget, LocationRecord, CoverageResult } from '../../types/spray';
import type { GeoJSONPolygon } from '../../types/orchard';
import orchardService from '../../services/orchard';
import sprayService from '../../services/spray';
import { TILE_LAYERS, MAP_MAX_ZOOM, type TileLayerKey } from '../../utils/leafletGeo';
import LayerToggleControl from '../map/LayerToggleControl';

interface SprayCoverageMapProps {
  /** Spray targets (farm / block / cultivar area). Boundaries are fetched per target. */
  targets?: SprayTarget[];
  /** When supplied, GPS waypoints are fetched for this spray. */
  sprayId?: number;
  /** Pre-resolved boundaries — skips the per-target fetch when provided. */
  targetBoundaries?: Record<string, GeoJSONPolygon>;
  /** Pre-loaded waypoints — skips the waypoint fetch when provided. */
  waypoints?: LocationRecord[];
  coverage?: CoverageResult | null;
  height?: string;
}

// Boundary colours by target type (matches the orchard map convention).
const BOUNDARY_COLOR: Record<string, string> = {
  farm: '#22c55e',
  block: '#3b82f6',
  cultivar_area: '#8b5cf6',
};

type LatLng = [number, number];

interface ResolvedBoundary {
  key: string;
  name: string;
  targetType: string;
  positions: LatLng[];
}

// GeoJSON polygons store [lng, lat]; Leaflet wants [lat, lng].
function ringToPositions(geometry: GeoJSONPolygon | null | undefined): LatLng[] {
  const ring = geometry?.coordinates?.[0];
  if (!Array.isArray(ring)) return [];
  return ring
    .filter((c) => Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]))
    .map((c) => [c[1], c[0]] as LatLng);
}

function toLatLng(record: LocationRecord): LatLng | null {
  const lat = parseFloat(String(record.latitude));
  const lng = parseFloat(String(record.longitude));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

function FitBounds({ positions }: { positions: LatLng[] }) {
  const map = useMap();
  const signature = positions.length
    ? `${positions.length}:${positions[0][0]},${positions[0][1]}`
    : 'empty';

  useEffect(() => {
    // No animation: the fit runs as boundaries and waypoints arrive, and an
    // animated zoom still in flight when the page changes leaves Leaflet
    // finishing a transition on a removed map (`_leaflet_pos` of undefined).
    if (positions.length > 1) {
      map.fitBounds(positions, { padding: [30, 30], animate: false });
    } else if (positions.length === 1) {
      map.setView(positions[0], 17, { animate: false });
    }
    // Refit only when the underlying data actually changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature]);

  return null;
}

/**
 * SprayCoverageMap renders:
 * - Target area boundaries (colour-coded by target type)
 * - GPS waypoint trail (green polyline with start/end markers)
 * - Uncovered zones from coverage analysis (red dots)
 *
 * Boundaries and waypoints are fetched here from the orchard and spray APIs —
 * the spray detail payload carries target ids only, not geometry.
 */
function SprayCoverageMap({
  targets = [],
  sprayId,
  targetBoundaries,
  waypoints: waypointsProp,
  coverage = null,
  height = '400px',
}: SprayCoverageMapProps) {
  const [fetchedBoundaries, setFetchedBoundaries] = useState<ResolvedBoundary[]>([]);
  const [fetchedWaypoints, setFetchedWaypoints] = useState<LocationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [mapLayer, setMapLayer] = useState<TileLayerKey>('satellite');

  // Advisors act on another org's data; orchard endpoints take it as a query param.
  const targetOrgId = sprayService.getTargetOrgId() ?? undefined;

  // Stable key so the fetch effect doesn't re-run on every parent render.
  const targetKey = useMemo(
    () => targets.map((t) => `${t.target_type}-${t.target_id}`).join(','),
    [targets]
  );

  // --- Load target boundaries -------------------------------------------------
  useEffect(() => {
    if (targetBoundaries) return;
    if (!targets.length) {
      setFetchedBoundaries([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      const results = await Promise.all(
        targets.map(async (target): Promise<ResolvedBoundary | null> => {
          try {
            const res =
              target.target_type === 'farm'
                ? await orchardService.getFarm(target.target_id, targetOrgId)
                : target.target_type === 'block'
                  ? await orchardService.getBlock(target.target_id, targetOrgId)
                  : await orchardService.getCultivarArea(target.target_id, targetOrgId);

            if (!res.success || !res.data) return null;
            const positions = ringToPositions(res.data.boundary_geometry);
            if (positions.length < 3) return null;

            return {
              key: `${target.target_type}-${target.target_id}`,
              name: target.target_name,
              targetType: target.target_type,
              positions,
            };
          } catch {
            return null; // one missing boundary shouldn't blank the whole map
          }
        })
      );

      if (cancelled) return;
      setFetchedBoundaries(results.filter((b): b is ResolvedBoundary => b !== null));
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      setLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, targetBoundaries, targetOrgId]);

  // --- Load GPS waypoints -----------------------------------------------------
  useEffect(() => {
    if (waypointsProp || !sprayId) return;

    let cancelled = false;
    sprayService
      .getWaypoints(sprayId)
      .then((res) => {
        if (cancelled) return;
        setFetchedWaypoints(res.success && Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setFetchedWaypoints([]);
      });

    return () => {
      cancelled = true;
    };
  }, [sprayId, waypointsProp]);

  // --- Derived render data ----------------------------------------------------
  const boundaries: ResolvedBoundary[] = useMemo(() => {
    if (!targetBoundaries) return fetchedBoundaries;
    return Object.entries(targetBoundaries)
      .map(([key, geometry]) => ({
        key,
        name: key,
        targetType: key.split('-')[0],
        positions: ringToPositions(geometry),
      }))
      .filter((b) => b.positions.length >= 3);
  }, [targetBoundaries, fetchedBoundaries]);

  const waypoints = waypointsProp ?? fetchedWaypoints;

  const trail: LatLng[] = useMemo(
    () => waypoints.map(toLatLng).filter((p): p is LatLng => p !== null),
    [waypoints]
  );

  const allPositions: LatLng[] = useMemo(
    () => [...boundaries.flatMap((b) => b.positions), ...trail],
    [boundaries, trail]
  );

  const uncoveredZones = useMemo(
    // Cap rendering for performance on large grids.
    () => (coverage?.uncovered_zones ?? []).slice(0, 500),
    [coverage]
  );

  const center: LatLng = allPositions.length
    ? [
        allPositions.reduce((s, p) => s + p[0], 0) / allPositions.length,
        allPositions.reduce((s, p) => s + p[1], 0) / allPositions.length,
      ]
    : [-37.8, 145.0];

  if (loading && !allPositions.length) {
    return (
      <div
        style={{ height }}
        className="bg-neutral-100 rounded-lg flex items-center justify-center text-neutral-400 text-sm"
      >
        Loading map…
      </div>
    );
  }

  if (!allPositions.length) {
    return (
      <div
        style={{ height }}
        className="bg-neutral-100 rounded-lg flex items-center justify-center text-neutral-400 text-sm text-center px-4"
      >
        No mapped boundaries or GPS waypoints for this spray yet.
      </div>
    );
  }

  const tiles = TILE_LAYERS[mapLayer];

  return (
    <div className="relative rounded-lg overflow-hidden border border-neutral-200">
      <MapContainer
        center={center}
        zoom={15}
        maxZoom={MAP_MAX_ZOOM}
        style={{ height, width: '100%' }}
        className="z-0"
        scrollWheelZoom
      >
        <TileLayer
          key={mapLayer}
          url={tiles.url}
          attribution={tiles.attribution}
          maxNativeZoom={tiles.maxNativeZoom}
          maxZoom={tiles.maxZoom}
        />

        <LayerToggleControl
          mapLayer={mapLayer}
          onToggle={() => setMapLayer((l) => (l === 'street' ? 'satellite' : 'street'))}
        />

        <FitBounds positions={allPositions} />

        {/* Target boundaries */}
        {boundaries.map((boundary) => (
          <Polygon
            key={boundary.key}
            positions={boundary.positions}
            pathOptions={{
              color: BOUNDARY_COLOR[boundary.targetType] ?? '#4f46e5',
              fillColor: BOUNDARY_COLOR[boundary.targetType] ?? '#4f46e5',
              fillOpacity: 0.12,
              weight: 2,
              dashArray: '6 4',
            }}
          >
            <Popup>{boundary.name}</Popup>
          </Polygon>
        ))}

        {/* GPS trail */}
        {trail.length > 1 && (
          <Polyline positions={trail} pathOptions={{ color: '#16a34a', weight: 3, opacity: 0.85 }} />
        )}

        {trail.length > 0 && (
          <Fragment>
            <CircleMarker
              center={trail[0]}
              radius={6}
              pathOptions={{ color: '#16a34a', fillColor: '#22c55e', fillOpacity: 1, weight: 2 }}
            >
              <Tooltip>Start</Tooltip>
            </CircleMarker>
            {trail.length > 1 && (
              <CircleMarker
                center={trail[trail.length - 1]}
                radius={6}
                pathOptions={{ color: '#dc2626', fillColor: '#f87171', fillOpacity: 1, weight: 2 }}
              >
                <Tooltip>End</Tooltip>
              </CircleMarker>
            )}
          </Fragment>
        )}

        {/* Uncovered zones */}
        {uncoveredZones.map((zone, idx) => (
          <CircleMarker
            key={`zone-${idx}`}
            center={[zone.lat, zone.lng]}
            radius={4}
            pathOptions={{ color: '#dc2626', fillColor: '#ef4444', fillOpacity: 0.4, weight: 0 }}
          />
        ))}
        <MapTouchGate />
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white/95 rounded-lg shadow-md px-3 py-2 text-xs space-y-1 z-[1000]">
        {boundaries.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 border-t-2 border-dashed border-blue-500" />
            <span className="text-neutral-600">Target area ({boundaries.length})</span>
          </div>
        )}
        {trail.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-green-600" />
            <span className="text-neutral-600">Spray path ({trail.length} pts)</span>
          </div>
        )}
        {uncoveredZones.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400 opacity-60" />
            <span className="text-neutral-600">Uncovered zones</span>
          </div>
        )}
        {coverage?.covered_pct != null && (
          <div className="pt-1 border-t border-neutral-100">
            <span
              className={`font-semibold ${
                coverage.covered_pct >= 90
                  ? 'text-success-600'
                  : coverage.covered_pct >= 70
                    ? 'text-amber-600'
                    : 'text-red-600'
              }`}
            >
              {coverage.covered_pct.toFixed(1)}% covered
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default SprayCoverageMap;
